import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const LANGS = [
  { code: "en-US", label: "EN", full: "English (US)" },
  { code: "es-US", label: "ES", full: "Español (US)" },
];

/**
 * Compact EN/ES language toggle. Persists choice in localStorage (via i18next).
 */
export default function LanguageToggle({ compact = false, dark = false }) {
  const { i18n, t } = useTranslation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  const current = LANGS.find((l) => i18n.language?.startsWith(l.code.slice(0, 2))) || LANGS[0];

  // Sync html[lang] attribute for accessibility
  useEffect(() => {
    document.documentElement.lang = current.code;
  }, [current.code]);

  const change = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
    // Persist the choice server-side so notifications reach the user in their
    // language. Best-effort; anonymous users just keep the localStorage choice.
    if (user) {
      api.patch("/users/me", { language: code.startsWith("es") ? "es" : "en" }).catch(() => {});
    }
  };

  return (
    <div className="relative" data-testid="language-toggle">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm text-xs font-display font-bold uppercase tracking-widest transition ${
          dark ? "text-cream/90 hover:bg-cream/10" : "text-worker hover:bg-linen"
        }`}
        aria-label={t("nav.changeLang")}
        data-testid="lang-toggle-btn"
      >
        <Globe size={14} />
        {compact ? current.label : current.label}
      </button>
      {open && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-30"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div className="absolute right-0 mt-1 z-40 bg-cream rounded-sm shadow-deep border border-sand min-w-[180px] py-1" data-testid="lang-menu">
            {LANGS.map((l) => (
              <button
                key={l.code}
                type="button"
                onClick={() => change(l.code)}
                className={`w-full text-left flex items-center justify-between gap-3 px-3 py-2 text-sm font-display hover:bg-linen ${
                  l.code === current.code ? "text-worker font-bold" : "text-smoke"
                }`}
                data-testid={`lang-option-${l.code}`}
              >
                <span>{l.full}</span>
                {l.code === current.code && <Check size={14} className="text-hive-ink" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
